import { Alarm, ThresholdMode, TriggerMode } from '../../gql/generated';
import {
  formatAlarmUnit,
  formatBooleanStateValue,
  isMachineAlarmSignal,
} from '../../utils/formatMetric';
import { machineDisplayName } from '../../utils/machineDisplay';
import { formatDateTime } from '../../utils/notificationFormatters';
import { InfoRow, InfoSectionHeader } from '../SidePanel';

interface AlarmDetailsTabProps {
  alarm: Alarm;
}

const thresholdModeLabels: Record<ThresholdMode, string> = {
  [ThresholdMode.AboveHigh]: 'Above high threshold',
  [ThresholdMode.BelowLow]: 'Below low threshold',
  [ThresholdMode.OutsideRange]: 'Outside configured range',
};

const triggerModeLabels: Record<TriggerMode, string> = {
  [TriggerMode.Percentage]: 'Percentage',
  [TriggerMode.Period]: 'Period',
  [TriggerMode.Spikes]: 'Spikes',
};

export function AlarmDetailsTab({ alarm }: AlarmDetailsTabProps) {
  const isMachineAlarm = isMachineAlarmSignal({
    unit: alarm.unit,
    signalCategory: alarm.signalCategory,
  });
  const unit = isMachineAlarm ? 'on/off' : formatAlarmUnit(alarm.unit);
  const thresholdModeLabel = isMachineAlarm
    ? alarm.thresholdMode === ThresholdMode.BelowLow
      ? 'Off state'
      : alarm.thresholdMode === ThresholdMode.AboveHigh
        ? 'On state'
        : 'On/off state'
    : thresholdModeLabels[alarm.thresholdMode];
  const machineNames = (alarm.machines || [])
    .map(machineDisplayName)
    .join(', ');
  const alertGroupNames = (alarm.alertGroups || [])
    .map((group) => group.name)
    .join(', ');

  return (
    <div className='flex flex-col'>
      <section>
        <InfoSectionHeader>Pulse alert</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Name' value={alarm.name} />
          <InfoRow label='Description' value={alarm.description} />
          <InfoRow
            label='Status'
            value={alarm.isEnabled ? 'Enabled' : 'Disabled'}
          />
          <InfoRow label='Triggered' value={alarm.isTriggered ? 'Yes' : 'No'} />
          <InfoRow label='Created' value={formatDateTime(alarm.createdAt)} />
          <InfoRow label='Owner' value={alarm.userId} />
        </div>
      </section>

      <section>
        <InfoSectionHeader>Signal</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Category' value={alarm.signalCategory} />
          <InfoRow label='Signal' value={alarm.signal} />
          <InfoRow label='Unit' value={unit} />
        </div>
      </section>

      <section>
        <InfoSectionHeader>Threshold & Trigger</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow
            label='Threshold mode'
            value={thresholdModeLabel}
          />
          <InfoRow
            label='Threshold low'
            value={
              isMachineAlarm
                ? formatBooleanStateValue(alarm.thresholdLow)
                : alarm.thresholdLow.toString()
            }
          />
          <InfoRow
            label='Threshold high'
            value={
              isMachineAlarm
                ? formatBooleanStateValue(alarm.thresholdHigh)
                : alarm.thresholdHigh.toString()
            }
          />
          <InfoRow
            label='Trigger mode'
            value={triggerModeLabels[alarm.triggerMode]}
          />
          <InfoRow
            label='Trigger value'
            value={alarm.triggerValue.toString()}
          />
        </div>
      </section>

      <section>
        <InfoSectionHeader>Evaluation windows</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Evaluation window' value={alarm.evalWindowSize} />
          <InfoRow label='Evaluation period' value={alarm.evalPeriod} />
          <InfoRow label='Cooldown period' value={alarm.cooldownPeriod} />
        </div>
      </section>

      <section>
        <InfoSectionHeader>Assignments</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow
            label='Machines'
            value={machineNames || 'No machines assigned'}
          />
          <InfoRow
            label='Alert groups'
            value={alertGroupNames || 'Not in a group'}
          />
        </div>
      </section>
    </div>
  );
}
